import isEmpty from "lodash/isEmpty";
import { Package, SelectedChecksType } from "../../../pages/order/OrderContext";
import { AddOnOrderSummary } from "./AddOnOrderSummary";
import uniqBy from "lodash/uniqBy";
import { List, ListItem } from "@certn/cascadia-react";
import { Text } from "@certn/cascadia";

type AddOnOrderSummaryContainerProps = {
  regularChecks: SelectedChecksType[];
  packageChecks: Package["checks"];
  currency?: string;
  canViewClientPortalPricing: boolean;
};

export const AddOnOrderSummaryContainer = ({
  regularChecks,
  packageChecks,
  currency,
  canViewClientPortalPricing,
}: AddOnOrderSummaryContainerProps) => {
  const isPackage = isEmpty(regularChecks) && !isEmpty(packageChecks);

  // Normalize checks ordered via regular flow or via a package
  const checks = isPackage
    ? packageChecks.map((check) => ({
        key: check.id,
        check_arguments: check.check_arguments ?? {},
        add_on_options: check.add_on_options,
      }))
    : regularChecks.map((check) => ({
        key: check.key,
        check_arguments: check.check_arguments ?? {},
        add_on_options: check.add_on_options,
      }));

  /**
   * Add-on options are deduplicated by key so that mutually inclusive
   * add-ons shared across checks (e.g. CV Review on both Employment and
   * Education) are only shown and counted once.
   */
  const allActiveAddOns = uniqBy(
    checks.flatMap((check) =>
      (check.add_on_options ?? []).filter(
        (addon) => !!check.check_arguments?.[addon.key]
      )
    ),
    "key"
  );

  if (allActiveAddOns.length === 0) return null;

  if (isPackage) {
    return (
      <List type="unordered">
        {allActiveAddOns.map((add_on) => (
          <ListItem key={add_on.key} size="secondary">
            <Text testID="summary-add-on-option">{add_on.name}</Text>
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <AddOnOrderSummary
      add_on_options={allActiveAddOns}
      check_arguments={Object.fromEntries(
        allActiveAddOns.map((a) => [a.key, true])
      )}
      currency={currency}
      canViewClientPortalPricing={canViewClientPortalPricing}
    />
  );
};
